import { lazy } from 'react';
import type { RouteConfig } from './types';
import { Navigate } from 'react-router-dom';

const SearchResults = lazy(() => import('../../shared/components/common/SearchForm/SearchResults'));
const ProfilePage = lazy(() => import('../../features/Profile/pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const CreateShipmentPage = lazy(() => import('../../features/Sender/pages/CreateShipment/CreateShipmentPage').then(m => ({ default: m.CreateShipmentPage })));
const MyItemsPage = lazy(() => import('../../features/Sender/pages/MyItems/MyItemsPage').then(m => ({ default: m.MyItemsPage })));
const ItemDetailsPage = lazy(() => import('../../features/Sender/pages/MyItems/ItemDetailsPage').then(m => ({ default: m.ItemDetailsPage })));
const EditShipmentPage = lazy(() => import('../../features/Sender/pages/MyItems/EditShipmentPage').then(m => ({ default: m.EditShipmentPage })));
const SenderOrderDetailsPage = lazy(() => import('../../features/Sender/pages/MyItems/OrderDetailsPage').then(m => ({ default: m.OrderDetailsPage })));
const DropOffPage = lazy(() => import('../../features/Sender/pages/MyItems/DropOffPage').then(m => ({ default: m.DropOffPage })));
const EditCartPage = lazy(() => import('../../features/Sender/pages/MyItems/EditCartPage').then(m => ({ default: m.EditCartPage })));
const EditItemPage = lazy(() => import('../../features/Sender/pages/MyItems/EditItemPage').then(m => ({ default: m.EditItemPage })));
const CreateItineraryPage = lazy(() => import('../../features/Traveller/pages/CreateItinerary/CreateItineraryPage').then(m => ({ default: m.CreateItineraryPage })));
const MyTravelsPage = lazy(() => import('../../features/Traveller/pages/MyTravels/MyTravelsPage').then(m => ({ default: m.MyTravelsPage })));
const ItineraryDetailsPage = lazy(() => import('../../features/Traveller/pages/MyTravels/ItineraryDetailsPage').then(m => ({ default: m.ItineraryDetailsPage })));
const EditItineraryPage = lazy(() => import('../../features/Traveller/pages/MyTravels/EditItineraryPage').then(m => ({ default: m.EditItineraryPage })));
const TravellerOrderDetailsPage = lazy(() => import('../../features/Traveller/pages/MyTravels/OrderDetailsPage').then(m => ({ default: m.OrderDetailsPage })));
const PickUpPage = lazy(() => import('../../features/Traveller/pages/MyTravels/PickUpPage').then(m => ({ default: m.PickUpPage })));
const ConfirmDeliveryPage = lazy(() => import('../../features/Traveller/pages/MyTravels/ConfirmDeliveryPage').then(m => ({ default: m.ConfirmDeliveryPage })));
const PaymentSuccess = lazy(() => import('../../features/Payment/PaymentSuccess'));
const PaymentCancel = lazy(() => import('../../features/Payment/PaymentCancel'));
const PayoneerComplete = lazy(() => import('../../features/Payment/PayoneerComplete'));
const NotFound = lazy(() => import('../../shared/components/common/NotFound').then(m => ({ default: m.NotFound })));
const ChatInbox = lazy(() => import('../../features/Chat/pages/ChatInbox').then(m => ({ default: m.ChatInbox })));
const GroupChatInbox = lazy(() => import('../../features/Chat/pages/GroupChatInbox').then(m => ({ default: m.GroupChatInbox })));
const ReceiverCheckoutPage = lazy(() => import('../../features/Receiver/pages/ReceiverCheckoutPage').then(m => ({ default: m.ReceiverCheckoutPage })));
const ReceiverItemsPage = lazy(() => import('../../features/Receiver/pages/ReceiverItemsPage').then(m => ({ default: m.ReceiverItemsPage })));
const ReceiverGroupChatPage = lazy(() => import('../../features/Receiver/pages/ReceiverGroupChatPage').then(m => ({ default: m.ReceiverGroupChatPage })));
const ReceiverGroupChatRoomPage = lazy(() => import('../../features/Receiver/pages/ReceiverGroupChatRoomPage').then(m => ({ default: m.ReceiverGroupChatRoomPage })));
const LogoutPage = lazy(() => import('../../features/Auth/pages/LogoutPage').then(m => ({ default: m.LogoutPage })));
const PublicSupportPage = lazy(() => import('../../features/Support/pages/PublicSupportPage').then(m => ({ default: m.PublicSupportPage })));

export const RoutingList: RouteConfig[] = [
  {
    route: '/',
    component: SearchResults,
    isProtected: false,
    layout: 'landing',
    showHeader: true,
    showFooter: true,
    showFAB: true,
  },

  {
    route: '/support',
    component: PublicSupportPage,
    isProtected: false,
    layout: 'landing',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/logout',
    component: LogoutPage,
    isProtected: false,
    layout: 'none',
    showHeader: false,
    showFooter: false,
    showFAB: false,
  },

  {
    route: '/update-profile',
    component: ProfilePage,
    isProtected: true,
    layout: 'main',
    showHeader: true,
    showFooter: true,
  },

  {
    route: '/create-shipment',
    component: CreateShipmentPage,
    isProtected: true,
    allowedRoles: ['sender'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/my-items',
    component: MyItemsPage,
    isProtected: true,
    allowedRoles: ['sender'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/my-items/:id/edit',
    component: EditShipmentPage,
    isProtected: true,
    allowedRoles: ['sender'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/my-items/:id',
    component: ItemDetailsPage,
    isProtected: true,
    allowedRoles: ['sender'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/my-orders',
    component: MyItemsPage,
    isProtected: true,
    allowedRoles: ['sender'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/order-details/:order_code',
    component: SenderOrderDetailsPage,
    isProtected: true,
    allowedRoles: ['sender'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/order-details/:order_code/drop-off',
    component: DropOffPage,
    isProtected: true,
    allowedRoles: ['sender'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/order-details/:order_code/edit-cart',
    component: EditCartPage,
    isProtected: true,
    allowedRoles: ['sender'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/order-details/:order_code/item/:item_id/edit',
    component: EditItemPage,
    isProtected: true,
    allowedRoles: ['sender'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/create-itinerary',
    component: CreateItineraryPage,
    isProtected: true,
    allowedRoles: ['traveller'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/my-travels',
    component: MyTravelsPage,
    isProtected: true,
    allowedRoles: ['traveller'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/wallet',
    element: <Navigate to="/update-profile?tab=wallet" replace />,
    isProtected: true,
    allowedRoles: ['traveller'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/my-travels/:id',
    component: ItineraryDetailsPage,
    isProtected: true,
    allowedRoles: ['traveller'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/my-travels/:id/edit',
    component: EditItineraryPage,
    isProtected: true,
    allowedRoles: ['traveller'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/traveller/order-details/:orderCode',
    component: TravellerOrderDetailsPage,
    isProtected: true,
    allowedRoles: ['traveller'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/traveller/order-details/:order_code/pick-up',
    component: PickUpPage,
    isProtected: true,
    allowedRoles: ['traveller'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/traveller/order-details/:orderCode/confirm-delivery',
    component: ConfirmDeliveryPage,
    isProtected: true,
    allowedRoles: ['traveller'],
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/chat/:roomId',
    component: ChatInbox,
    isProtected: true,
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/group-chat/:roomId',
    component: GroupChatInbox,
    isProtected: true,
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '/receiver/checkout',
    component: ReceiverCheckoutPage,
    isProtected: false,
    layout: 'none',
    showHeader: false,
    showFooter: false,
    showFAB: false,
  },

  {
    route: '/receiver/items',
    component: ReceiverItemsPage,
    isProtected: false,
    layout: 'none',
    showHeader: false,
    showFooter: false,
    showFAB: false,
  },

  {
    route: '/receiver/group_chat',
    component: ReceiverGroupChatPage,
    isProtected: false,
    layout: 'none',
    showHeader: false,
    showFooter: false,
    showFAB: false,
  },

  {
    route: '/receiver/group_chat/inbox',
    component: ReceiverGroupChatRoomPage,
    isProtected: false,
    layout: 'none',
    showHeader: false,
    showFooter: false,
    showFAB: false,
  },

  {
    route: '/payment/success',
    component: PaymentSuccess,
    isProtected: false,
    layout: 'none',
    showHeader: false,
    showFooter: false,
    showFAB: false,
  },

  {
    route: '/payment/cancel',
    component: PaymentCancel,
    isProtected: false,
    layout: 'none',
    showHeader: false,
    showFooter: false,
    showFAB: false,
  },

  {
    route: '/payouts/payoneer/complete',
    component: PayoneerComplete,
    isProtected: true,
    layout: 'main',
    showHeader: true,
    showFooter: true,
    showFAB: false,
  },

  {
    route: '*',
    component: NotFound,
    isProtected: false,
    layout: 'none',
  },
];
